// background-smoke.js - A non-blocking distant smoke effect for threejs-gallery
import * as THREE from 'three';

export class BackgroundSmoke {
  constructor(scene, camera) {
    this.scene = scene;
    this.camera = camera;
    this.clock = new THREE.Clock();
    this.smokePlanes = [];
    this.smokeLights = []; 
    
    // Different settings for desktop vs mobile
    this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    this.isMobileSafari = /iPhone|iPad|iPod/i.test(navigator.userAgent) && 
                          /AppleWebKit/i.test(navigator.userAgent) && 
                          !window.MSStream;
    
    // Desktop settings (original)
    if (!this.isMobileSafari) {
      this.planeCount = 12; 
      this.minDistance = 30; 
      this.maxDistance = 60; 
      this.planeHeight = 10; 
      this.planeWidth = 60;
    } 
    // Mobile settings
    else {
      this.planeCount = 10;
      this.minDistance = 25;
      this.maxDistance = 50;
      this.planeHeight = 30;
      this.planeWidth = 60;
    }
    
    console.log("Device detection:", {
      userAgent: navigator.userAgent,
      isMobile: this.isMobile,
      isMobileSafari: this.isMobileSafari
    });
    
    // Create a dedicated layer for smoke
    this.smokeLayer = new THREE.Group();
    this.smokeLayer.renderOrder = -1;
    this.smokeLayer.layers.set(1);
    scene.add(this.smokeLayer);
    
    // Initialize with appropriate technique
    if (this.isMobileSafari) {
      console.log("Using enhanced mobile smoke implementation");
      this.initEnhancedMobileSmoke();
    } else {
      console.log("Using original desktop smoke implementation");
      this.initDesktopSmoke();
    }
  }
  
  // Enhanced implementation for mobile Safari
  initEnhancedMobileSmoke() {
    // Create multiple animated textures for variety
    const textures = [
      this.createMobileTexture(0.7, 0.3),
      this.createMobileTexture(0.5, 0.5),
      this.createMobileTexture(0.6, 0.4)
    ];
    
    // Create planes in a more interesting arrangement
    const positions = [
      // Main cardinal directions
      { x: 0, z: this.minDistance, angle: 0, scale: 1.0 },
      { x: 0, z: -this.minDistance, angle: Math.PI, scale: 1.0 },
      { x: this.minDistance, z: 0, angle: Math.PI/2, scale: 1.0 },
      { x: -this.minDistance, z: 0, angle: -Math.PI/2, scale: 1.0 },
      
      // Diagonals - farther out
      { x: this.minDistance * 0.7, z: this.minDistance * 0.7, angle: Math.PI/4, scale: 1.2 },
      { x: -this.minDistance * 0.7, z: this.minDistance * 0.7, angle: -Math.PI/4, scale: 1.2 },
      { x: this.minDistance * 0.7, z: -this.minDistance * 0.7, angle: 3*Math.PI/4, scale: 1.2 },
      { x: -this.minDistance * 0.7, z: -this.minDistance * 0.7, angle: -3*Math.PI/4, scale: 1.2 }
    ];
    
    // Add some higher planes for more vertical coverage
    positions.push(
      { x: 0, z: this.minDistance * 1.2, angle: 0, scale: 1.5, y: this.planeHeight * 0.7 },
      { x: 0, z: -this.minDistance * 1.2, angle: Math.PI, scale: 1.5, y: this.planeHeight * 0.7 }
    );
    
    positions.forEach((pos, index) => {
      // Select a texture with some variation
      const texture = textures[index % textures.length];
      
      // Create a plane with more interesting properties
      const planeWidth = this.planeWidth * pos.scale;
      const planeHeight = this.planeHeight * (pos.scale || 1.0);
      const planeGeometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
      
      // Create material with animated properties
      const planeMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: 0.4 + (index % 3) * 0.1, // Varied opacity
        depthWrite: false,
        depthTest: true,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        color: new THREE.Color(
          0.3 + Math.random() * 0.2,
          0.5 + Math.random() * 0.3,
          0.7 + Math.random() * 0.3
        ) // Varied blue colors
      });
      
      const plane = new THREE.Mesh(planeGeometry, planeMaterial);
      
      // Position with some variety
      const yPos = pos.y || (this.planeHeight/4 - 2 + index % 3);
      plane.position.set(pos.x, yPos, pos.z);
      
      // Rotate to face center with slight random variation
      const lookAtPos = new THREE.Vector3(
        Math.random() * 2 - 1,
        yPos,
        Math.random() * 2 - 1
      );
      plane.lookAt(lookAtPos);
      
      // Add slight rotation for variety
      plane.rotation.z = (Math.random() - 0.5) * 0.2;
      
      plane.renderOrder = -1000;
      plane.layers.set(1);
      
      this.smokeLayer.add(plane);
      
      // Store with enhanced animation parameters
      this.smokePlanes.push({
        mesh: plane,
        initialPosition: { x: pos.x, y: yPos, z: pos.z },
        animParams: {
          offsetX: index * 1000,
          offsetY: index * 500,
          speedX: 0.02 + Math.random() * 0.02,
          speedY: 0.01 + Math.random() * 0.01,
          rotSpeed: 0.005 + Math.random() * 0.01,
          amplitude: 0.5 + Math.random() * 0.5,
          scaleSpeed: 0.03 + Math.random() * 0.02,
          scaleAmount: 0.05 + Math.random() * 0.05,
          opacitySpeed: 0.04 + Math.random() * 0.04,
          opacityAmount: 0.15 + Math.random() * 0.05
        }
      });
      
      // Create lights
      if (index < 6) { // Fewer lights for performance
        const light = new THREE.PointLight(
          new THREE.Color(0.4 + Math.random() * 0.2, 0.6 + Math.random() * 0.2, 0.8 + Math.random() * 0.2),
          1.0 + Math.random() * 1.0,
          40 + Math.random() * 20
        );
        light.position.set(
          pos.x + (Math.random() - 0.5) * 5, 
          yPos + Math.random() * 5, 
          pos.z + (Math.random() - 0.5) * 5
        );
        light.layers.set(1);
        this.smokeLayer.add(light);
        
        this.smokeLights.push({
          light: light,
          initialPosition: light.position.clone(),
          animParams: {
            offsetX: index * 1000,
            offsetY: index * 500,
            speedX: 0.05 + Math.random() * 0.05,
            speedY: 0.03 + Math.random() * 0.03,
            speedZ: 0.04 + Math.random() * 0.04,
            amplitude: 2 + Math.random() * 2,
            intensitySpeed: 0.1 + Math.random() * 0.1,
            intensityAmount: 0.4 + Math.random() * 0.2,
            colorChangeSpeed: 0.02 + Math.random() * 0.02
          }
        });
      }
    });
    
    console.log(`Created ${this.smokePlanes.length} enhanced smoke planes for mobile`);
  }
  
  // ORIGINAL DESKTOP IMPLEMENTATION
  initDesktopSmoke() {
    // Create a gradient texture for the smoke
    const smokeTexture = this.createSmokeTexture();
    
    // Calculate smoke plane positions in a radial arrangement
    const angleStep = (Math.PI * 2) / this.planeCount;
    
    // Create dense arrangement of smoke planes
    // First layer - closer to camera
    for (let i = 0; i < this.planeCount; i++) {
      const angle = i * angleStep;
      
      // Calculate position for first layer (closer to camera)
      const x1 = Math.sin(angle) * this.minDistance;
      const z1 = Math.cos(angle) * this.minDistance;
      
      // Create smoke plane at this position
      this.createSmokePlane(x1, z1, angle, smokeTexture);
      
      // Add a subtle light to illuminate the smoke (optional)
      const lightY1 = this.planeHeight / 3 - 3 + Math.random() * 5;
      this.createSmokeLight(x1, lightY1, z1);
    }
    
    // Second layer - farther from camera
    for (let i = 0; i < this.planeCount; i++) {
      const angle = i * angleStep + (angleStep / 2); // Offset angle for better coverage
      
      // Calculate position for second layer (medium distance)
      const x2 = Math.sin(angle) * ((this.minDistance + this.maxDistance) / 2);
      const z2 = Math.cos(angle) * ((this.minDistance + this.maxDistance) / 2);
      
      // Create smoke plane at this position
      this.createSmokePlane(x2, z2, angle, smokeTexture);
    }
    
    // Third layer - farthest from camera
    for (let i = 0; i < this.planeCount/2; i++) {
      const angle = i * (angleStep*2); // Sparser at distance
      
      // Calculate position for third layer (farther)
      const x3 = Math.sin(angle) * this.maxDistance;
      const z3 = Math.cos(angle) * this.maxDistance;
      
      // Create smoke plane at this position
      this.createSmokePlane(x3, z3, angle, smokeTexture);
    }
  }
  
  // Create mobile textures
  createMobileTexture(centerOpacity = 0.7, edgeFeather = 0.4) {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const context = canvas.getContext('2d');
    
    // Clear canvas
    context.clearRect(0, 0, canvas.width, canvas.height);
    
    // Create a more complex gradient with multiple steps
    const gradient = context.createRadialGradient(
      canvas.width/2, canvas.height/2, 0,
      canvas.width/2, canvas.height/2, canvas.width/2
    );
    
    // Add more color stops for visual interest
    gradient.addColorStop(0, 'rgba(255, 255, 255, ' + centerOpacity + ')');
    gradient.addColorStop(0.3, 'rgba(20, 255, 255, ' + (centerOpacity * 0.9) + ')');
    gradient.addColorStop(0.6, 'rgba(255, 255, 255, ' + (centerOpacity * 0.6) + ')');
    gradient.addColorStop(0.8, 'rgba(255, 255, 255, ' + (centerOpacity * 0.3) + ')');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add some random noise for texture
    const addNoise = (intensity) => {
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      for (let i = 0; i < data.length; i += 4) {
        // Only add noise where there's some opacity
        if (data[i + 3] > 0) {
          const noise = Math.random() * intensity - intensity/2;
          data[i] = Math.max(0, Math.min(255, data[i] + noise)); // r
          data[i+1] = Math.max(0, Math.min(255, data[i+1] + noise)); // g
          data[i+2] = Math.max(0, Math.min(255, data[i+2] + noise)); // b
        }
      }
      
      context.putImageData(imageData, 0, 0);
    };
    
    // Add subtle noise
    addNoise(20);
    
    const texture = new THREE.Texture(canvas);
    texture.needsUpdate = true;
    return texture;
  }
  
  // ORIGINAL DESKTOP IMPLEMENTATION METHODS
  
  // Create a light to subtly illuminate smoke
  createSmokeLight(x, y, z) {
    // Create a bright point light
    const light = new THREE.PointLight(
      0xffffff, // Bright cyan-blue tint
      1.5,      // Moderate intensity
      30,       // Good reach
      2         // Fast falloff
    );
    
    // Position light
    light.position.set(x, y, z);
    
    // Set light to only affect the smoke layer
    light.layers.set(1);
    
    // Add to smoke layer group
    this.smokeLayer.add(light);
    
    // Store light with its position
    this.smokeLights.push({
      light: light,
      initialPosition: { x, y, z }
    });
  }
  
  createSmokePlane(x, z, angle, texture) {
    // Create smoke particles instead of planes for better visibility
    const particles = new THREE.Points(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-this.planeWidth/2, -this.planeHeight/2, 0),
        new THREE.Vector3(this.planeWidth/2, -this.planeHeight/2, 0),
        new THREE.Vector3(this.planeWidth/2, this.planeHeight/2, 0),
        new THREE.Vector3(-this.planeWidth/2, this.planeHeight/2, 0),
        // Add more points for a denser effect
        new THREE.Vector3(-this.planeWidth/4, -this.planeHeight/4, 0),
        new THREE.Vector3(this.planeWidth/4, -this.planeHeight/4, 0),
        new THREE.Vector3(this.planeWidth/4, this.planeHeight/4, 0),
        new THREE.Vector3(-this.planeWidth/4, this.planeHeight/4, 0),
        // Center point
        new THREE.Vector3(0, 0, 0),
        // Add fewer random points for a sparser effect
        ...Array(10).fill().map(() => new THREE.Vector3(
          (Math.random() - 0.5) * this.planeWidth,
          (Math.random() - 0.5) * this.planeHeight,
          (Math.random() - 0.5) * 5
        ))
      ]),
      new THREE.PointsMaterial({
        map: texture,
        size: 40, // Smaller points for subtler effect
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.6, // Lower opacity
        depthWrite: false, // Don't write to depth buffer
        depthTest: true,  // DO test against depth buffer
        blending: THREE.AdditiveBlending,
        color: 0xdddddd // Softer blue
      })
    );
    
    // Position particles
    particles.position.set(x, this.planeHeight / 3 - 4, z);
    
    // Set render order to ensure smoke renders behind objects
    particles.renderOrder = -1000;
    
    // Set to layer 1 (not the default layer 0 used for clickable objects)
    particles.layers.set(1);
    
    // Add to smoke layer group
    this.smokeLayer.add(particles);
    
    // Store in array for animation
    this.smokePlanes.push({
      mesh: particles,
      initialPosition: { x, y: this.planeHeight / 3 - 4, z },
      angle: angle,
      // Add properties for animation with more movement
      animParams: {
        offsetX: Math.random() * 1000,
        offsetY: Math.random() * 1000,
        speedX: Math.random() * 0.08 + 0.05, // Faster movement
        speedY: Math.random() * 0.06 + 0.03, // Faster movement
        amplitude: Math.random() * 1.2 + 0.8 // Larger movement
      }
    });
  }
  
  createSmokeTexture() {
    // Create a canvas for the texture
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext('2d');
    
    // Fill with black background
    context.fillStyle = 'black';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Create a radial gradient (bright circle)
    const gradient = context.createRadialGradient(
      canvas.width / 2, canvas.height / 2, 0,
      canvas.width / 2, canvas.height / 2, canvas.width / 2
    );
    
    // Make it bright white in center fading to transparent
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1.0)');
    gradient.addColorStop(0.2, 'rgba(200, 240, 255, 0.9)');
    gradient.addColorStop(0.5, 'rgba(150, 200, 255, 0.5)');
    gradient.addColorStop(0.8, 'rgba(100, 150, 255, 0.2)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    
    // Fill with gradient
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Create texture from canvas
    const texture = new THREE.Texture(canvas);
    texture.needsUpdate = true;
    
    return texture;
  }
  
  update() {
    if (this.smokePlanes.length === 0) return;
    
    const elapsedTime = this.clock.getElapsedTime();
    
    if (this.isMobileSafari) {
      // MOBILE UPDATE LOGIC
      this.updateMobileSmoke(elapsedTime);
    } else {
      // DESKTOP UPDATE LOGIC - ORIGINAL VERSION
      this.updateDesktopSmoke(elapsedTime);
    }
  }
  
  // Mobile update logic
  updateMobileSmoke(elapsedTime) {
    // Update smoke planes with enhanced animations
    for (let i = 0; i < this.smokePlanes.length; i++) {
      const plane = this.smokePlanes[i];
      const params = plane.animParams;
      const mesh = plane.mesh;
      
      // Position animation
      const xOffset = Math.sin(elapsedTime * params.speedX + params.offsetX) * params.amplitude;
      const yOffset = Math.sin(elapsedTime * params.speedY + params.offsetY) * (params.amplitude * 0.5);
      
      mesh.position.x = plane.initialPosition.x + xOffset;
      mesh.position.y = plane.initialPosition.y + yOffset;
      
      // Rotation animation
      if (params.rotSpeed) {
        mesh.rotation.z += params.rotSpeed * Math.sin(elapsedTime * 0.2 + i);
      }
      
      // Scale animation
      if (params.scaleSpeed && params.scaleAmount) {
        const scale = 1.0 + Math.sin(elapsedTime * params.scaleSpeed + i) * params.scaleAmount;
        mesh.scale.set(scale, scale, 1);
      }
      
      // Update material properties
      if (mesh.material) {
        // Opacity animation
        if (params.opacitySpeed && params.opacityAmount) {
          const baseOpacity = 0.4;
          mesh.material.opacity = baseOpacity + 
            Math.sin(elapsedTime * params.opacitySpeed + i) * params.opacityAmount;
        } else {
          const baseOpacity = 0.4;
          mesh.material.opacity = baseOpacity + 
            Math.sin(elapsedTime * 0.1 + i) * 0.1;
        }
        
        // Update shader
        mesh.material.needsUpdate = true;
      }
    }
    
    // Update lights with enhanced animations
    for (let i = 0; i < this.smokeLights.length; i++) {
      const lightObj = this.smokeLights[i];
      if (!lightObj.light) continue;
      
      const light = lightObj.light;
      const pos = lightObj.initialPosition;
      const params = lightObj.animParams || {};
      
      // Position animation
      if (params.speedX && params.amplitude) {
        light.position.x = pos.x + Math.sin(elapsedTime * params.speedX + params.offsetX) * params.amplitude;
        light.position.y = pos.y + Math.sin(elapsedTime * params.speedY + params.offsetY) * params.amplitude;
        light.position.z = pos.z + Math.sin(elapsedTime * (params.speedZ || params.speedX) + params.offsetX) * params.amplitude;
      } else {
        light.position.x = pos.x + Math.sin(elapsedTime * 0.2 + i) * 1.0;
        light.position.y = pos.y + Math.sin(elapsedTime * 0.1 + i) * 0.5;
        light.position.z = pos.z + Math.sin(elapsedTime * 0.15 + i) * 1.0;
      }
      
      // Intensity animation
      if (params.intensitySpeed && params.intensityAmount) {
        const baseIntensity = 1.0;
        light.intensity = baseIntensity + 
          Math.sin(elapsedTime * params.intensitySpeed + i) * params.intensityAmount;
      } else {
        light.intensity = 1.0 + Math.sin(elapsedTime * 0.1 + i) * 0.3;
      }
      
      // Color animation
      if (params.colorChangeSpeed) {
        const hue = (elapsedTime * params.colorChangeSpeed + i * 0.1) % 1;
        light.color.setHSL(0.5 + hue * 0.2, 0.7, 0.5);
      }
    }
  }
  
  // Desktop update logic - ORIGINAL
  updateDesktopSmoke(elapsedTime) {
    // Update each smoke particle system
    for (let i = 0; i < this.smokePlanes.length; i++) {
      const smokePlane = this.smokePlanes[i];
      const params = smokePlane.animParams;
      const particles = smokePlane.mesh;
      
      // Apply subtle movement
      const xOffset = Math.sin(elapsedTime * params.speedX + params.offsetX) * params.amplitude;
      const yOffset = Math.sin(elapsedTime * params.speedY + params.offsetY) * (params.amplitude * 0.5);
      
      // Update position with oscillation
      particles.position.x = smokePlane.initialPosition.x + xOffset;
      particles.position.y = smokePlane.initialPosition.y + yOffset;
      
      // Rotate particles for more dynamic effect
      particles.rotation.z = Math.sin(elapsedTime * 0.1) * 0.05;
      
      // Make particles grow and shrink
      const scale = 1.0 + Math.sin(elapsedTime * 0.2) * 0.2;
      particles.scale.set(scale, scale, 1);
      
      // Update particles appearance
      if (particles.material) {
        // Make size pulse more subtly
        particles.material.size = 40 + Math.sin(elapsedTime * 0.2 + i) * 15;
        
        // Animate opacity for subtle pulsing
        const opacityBase = 0.6;
        const opacityVariation = 0.15;
        const opacitySpeed = 0.1;
        
        particles.material.opacity = opacityBase + 
          Math.sin(elapsedTime * opacitySpeed + i * 0.2) * opacityVariation;
          
        // Make sure changes take effect
        particles.material.needsUpdate = true;
      }
    }
    
    // Update light positions with subtle movement
    for (let i = 0; i < this.smokeLights.length; i++) {
      if (i < this.smokePlanes.length) {
        const light = this.smokeLights[i].light;
        const initialPos = this.smokeLights[i].initialPosition;
        
        // Add wobble to the light position
        const wobbleX = Math.sin(elapsedTime * 0.3 + i * 2) * 2;
        const wobbleY = Math.sin(elapsedTime * 0.2 + i * 3) * 1.5;
        const wobbleZ = Math.sin(elapsedTime * 0.25 + i * 4) * 2;
        
        // Update light position
        light.position.x = initialPos.x + wobbleX;
        light.position.y = initialPos.y + wobbleY;
        light.position.z = initialPos.z + wobbleZ;
        
        // Make lights pulse gently
        const baseIntensity = 1.5; // Moderate intensity
        const intensityVariation = 0.5;
        
        light.intensity = baseIntensity + 
          Math.sin(elapsedTime * 0.15 + i * 0.5) * intensityVariation;
          
        // Gradually shift light color
        const color = new THREE.Color().setHSL(0.6, 0.8, 0.5); // Blue-cyan hue
        light.color = color;
      }
    }
  }
  
  // Clean up resources
  dispose() {
    this.smokePlanes.forEach(plane => {
      if (plane.mesh) {
        if (plane.mesh.geometry) {
          plane.mesh.geometry.dispose();
        }
        
        if (plane.mesh.material) {
          if (plane.mesh.material.map) {
            plane.mesh.material.map.dispose();
          }
          plane.mesh.material.dispose();
        }
      }
    });
    
    this.smokeLights.forEach(lightObj => {
      if (lightObj.light) {
        this.smokeLayer.remove(lightObj.light);
      }
    });
    
    this.scene.remove(this.smokeLayer);
    
    this.smokePlanes = [];
    this.smokeLights = [];
  }
}